// src/components/product/BundleEditor/BundleComponentPicker.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, Minus, Plus, Trash2, ChevronRight, ArrowLeft, Hash, Package } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getProducts, getProductVariations } from '../../../services/api/productSearch';
import { componentKey } from './bundleUtils';
import BundleCheckbox from './BundleCheckbox';
import styles from './BundleEditor.module.css';

const isVariable = (p) => p.type === 'variable' || p.productType === 'variable';

const BundleComponentPicker = ({
  bundleItems,
  bundleProductId,
  priceMap = null,
  priceShopName,
  selectedKeys,
  onAdd,
  onRemove,
  onQtyChange,
  onToggleSelect,
  onToggleSelectAll,
}) => {
  const { t } = useLocalization();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [variationParent, setVariationParent] = useState(null);
  const [variations, setVariations] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!searchTerm.trim()) {
      setResults([]);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await getProducts({ search: searchTerm.trim(), limit: 20, page: 1 });
        setResults((res.products || []).filter(p =>
          p.type !== 'bundle' && String(p.productId || p.id) !== String(bundleProductId)));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [searchTerm, bundleProductId]);

  const reset = () => {
    setSearchTerm('');
    setResults([]);
    setVariationParent(null);
    setVariations([]);
  };

  const handleSelect = async (product) => {
    const productId = product.productId || product.id;
    const name = product.displayName || product.name;
    if (isVariable(product) && !product.variationId) {
      setVariationParent({ productId, name });
      setSearching(true);
      try {
        const res = await getProductVariations(productId);
        setVariations(res.variations || []);
      } catch {
        setVariations([]);
      } finally {
        setSearching(false);
      }
      return;
    }
    onAdd({ productId, variationId: product.variationId || null, name, sku: product.sku });
    reset();
  };

  const handleSelectVariation = (variation) => {
    onAdd({
      productId: variationParent.productId,
      variationId: variation.id,
      name: variation.displayName || `${variationParent.name} (${variation.attributeDisplay || variation.sku || variation.id})`,
      sku: variation.sku,
    });
    reset();
  };

  const allSelected = bundleItems.length > 0 && selectedKeys.length === bundleItems.length;

  const renderResultRow = (key, item, name, onClick, hasChildren = false, priceKey = null) => {
    const stock = item.stockQuantity;
    const hasStock = stock !== null && stock !== undefined && !hasChildren;
    const outOfStock = hasStock && Number(stock) <= 0;
    const shopPrice = priceKey && priceMap ? priceMap[priceKey] : undefined;
    const price = shopPrice !== undefined && shopPrice !== null
      ? `Rs. ${(parseFloat(shopPrice) || 0).toFixed(2)}`
      : t('products.notAssigned');
    return (
      <button
        type="button"
        key={key}
        className={`${styles.resultItem} ${outOfStock ? styles.outOfStock : ''}`}
        onClick={onClick}
      >
        <span className={styles.productInfo}>
          <span className={styles.productName}>{name}</span>
          <span className={styles.productDetails}>
            <span className={styles.productSku}><Hash size={12} />{item.sku || t('products.noSku', 'No SKU')}</span>
            {hasStock && (
              <span className={styles.productStock}>
                <Package size={12} />
                {outOfStock ? t('products.outOfStock') : `${stock} ${t('products.inStock')}`}
              </span>
            )}
          </span>
        </span>
        {hasChildren ? <ChevronRight size={14} /> : (
          <span className={styles.productPrice} title={priceShopName || undefined}>
            {price}
          </span>
        )}
      </button>
    );
  };

  const renderResults = () => {
    if (searching) {
      return <div className={styles.noResults}><Loader2 size={16} className={styles.spinner} /></div>;
    }
    if (variationParent) {
      return (
        <div className={styles.resultsList}>
          <button type="button" className={styles.resultItem} onClick={() => setVariationParent(null)}>
            <span className={styles.productInfo}><span className={styles.productName}><ArrowLeft size={14} /> {variationParent.name}</span></span>
          </button>
          {variations.map(v => renderResultRow(
            v.id, v, v.displayName || v.attributeDisplay || v.sku, () => handleSelectVariation(v),
            false, componentKey(variationParent.productId, v.id),
          ))}
        </div>
      );
    }
    if (results.length === 0) {
      return <div className={styles.noResults}>{t('products.noProductsFound', 'No products found')}</div>;
    }
    return (
      <div className={styles.resultsList}>
        {results.map((p, i) => renderResultRow(
          `${p.productId || p.id}-${p.variationId || i}`,
          p,
          p.displayName || p.name,
          () => handleSelect(p),
          isVariable(p) && !p.variationId,
          componentKey(p.productId || p.id, p.variationId),
        ))}
      </div>
    );
  };

  return (
    <div className={styles.pickerSection}>
      <h3 className={styles.sectionTitle}>{t('products.bundleComponents')}</h3>

      <div className={styles.searchWrapper}>
        <div className={styles.searchInputWrapper}>
          <Search size={16} className={styles.searchIcon} />
          <input
            type="text"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setVariationParent(null); }}
            placeholder={t('products.searchComponents')}
            className={styles.searchInput}
          />
          {searchTerm && (
            <button type="button" className={styles.clearSearchButton} onClick={reset}><X size={14} /></button>
          )}
        </div>
        {searchTerm.trim() && <div className={styles.searchResults}>{renderResults()}</div>}
      </div>

      {bundleItems.length === 0 ? (
        <p className={styles.emptyHint}>{t('products.noComponentsYet')}</p>
      ) : (
        <>
          <div className={styles.componentList}>
            {bundleItems.map(item => (
              <div key={item.key} className={styles.componentRow}>
                <BundleCheckbox
                  checked={selectedKeys.includes(item.key)}
                  onChange={() => onToggleSelect(item.key)}
                />
                <div className={styles.componentInfo}>
                  <h4 className={styles.itemName}>{item.name}</h4>
                  {item.sku && (
                    <span className={styles.itemSku}>
                      <Hash size={12} />
                      {item.sku}
                    </span>
                  )}
                </div>
                <div className={styles.qtyControl}>
                  <button type="button" onClick={() => onQtyChange(item.key, Math.max(1, (parseFloat(item.quantity) || 1) - 1))}>
                    <Minus size={14} />
                  </button>
                  <input
                    type="number"
                    min="0.01"
                    step="any"
                    value={item.quantity}
                    onChange={e => onQtyChange(item.key, e.target.value)}
                    aria-label={t('products.componentQty')}
                  />
                  <button type="button" onClick={() => onQtyChange(item.key, (parseFloat(item.quantity) || 0) + 1)}>
                    <Plus size={14} />
                  </button>
                </div>
                <button type="button" className={styles.removeButton} onClick={() => onRemove([item.key])}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          <div className={styles.pickerFooter}>
            <BundleCheckbox
              checked={allSelected}
              onChange={onToggleSelectAll}
              label={t('products.selectAll')}
              className={styles.selectAllLabel}
            />
            <span className={styles.selectionCount}>{t('products.selectedCount', { count: selectedKeys.length })}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default BundleComponentPicker;
